import { getFullName } from '@provision/formatting-utils';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { AvatarUploader } from 'src/components/CreateUserModal/avatar-uploader';
import {
  AvatarFlushFailureReason,
  AvatarFlushStatus,
  useAvatarSelection,
} from 'src/components/CreateUserModal/use-avatar-selection';
import { getUserModalActions } from 'src/components/CreateUserModal/user-modal-actions';
import {
  EMAIL_VALIDATION_REGEX,
  FormPhoneInput,
  FormTextField,
  MAX_NUMBER_LENGTH,
  MIN_NUMBER_LENGTH,
} from 'src/components/Forms';
import { appendCodeToPhoneNumber } from 'src/components/Forms/utils';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { getPlatformUserAvatarStorageKey } from 'src/graphql/entities/platform-user/platform-user-avatar-url';
import { useDeletePlatformUserAvatar } from 'src/graphql/entities/platform-user/use-delete-platform-user-avatar';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { CreateUserFormFields, CreateUserFormModel } from './types';

export const CreateUserModal = () => {
  const { closeModal } = useModalMethods(Modals.CreateUser);
  const { isOpened, modalProps } = useModalState(Modals.CreateUser);

  const formMethods = useForm<CreateUserFormModel>({
    mode: 'all',
    values: {
      [CreateUserFormFields.FirstName]: '',
      [CreateUserFormFields.LastName]: '',
      [CreateUserFormFields.Email]: '',
      [CreateUserFormFields.PhoneNumber]: '',
      [CreateUserFormFields.AvatarFileKey]: '',
    },
  });

  const { formState, reset, handleSubmit, watch } = formMethods;
  const firstName = watch(CreateUserFormFields.FirstName);
  const lastName = watch(CreateUserFormFields.LastName);
  const previewName = getFullName({ firstName, lastName }) || 'New User';

  const avatar = useAvatarSelection();

  const closeModalAndResetForm = useCallback(() => {
    reset();
    avatar.reset();
    closeModal();
  }, [reset, avatar, closeModal]);

  const { deleteAvatar } = useDeletePlatformUserAvatar();
  const { showErrorAlert } = useNotifications();

  const onFormSubmit = useCallback(
    async (formValues: CreateUserFormModel) => {
      const flush = await avatar.flush(modalProps?.partnerId);
      if (flush.status === AvatarFlushStatus.Failed) {
        if (flush.reason === AvatarFlushFailureReason.MissingPartner) {
          showErrorAlert('Cannot upload the photo: missing partner. Please reopen the form.');
        }
        return;
      }
      const avatarFileKey =
        flush.status === AvatarFlushStatus.Uploaded
          ? flush.fileKey
          : formValues[CreateUserFormFields.AvatarFileKey] || undefined;

      const preparedFormValues = {
        ...formValues,
        [CreateUserFormFields.FirstName]: formValues[CreateUserFormFields.FirstName].trim(),
        [CreateUserFormFields.LastName]: formValues[CreateUserFormFields.LastName].trim(),
        [CreateUserFormFields.PhoneNumber]: formValues[CreateUserFormFields.PhoneNumber]
          ? appendCodeToPhoneNumber(formValues[CreateUserFormFields.PhoneNumber])
          : undefined,
        [CreateUserFormFields.AvatarFileKey]: avatarFileKey,
      };
      try {
        await modalProps?.onSubmit(preparedFormValues);
        closeModalAndResetForm();
      } catch {
        if (flush.status === AvatarFlushStatus.Uploaded) {
          void deleteAvatar(
            getPlatformUserAvatarStorageKey(modalProps?.partnerId, flush.fileKey),
          );
        }
      }
    },
    [avatar, modalProps, closeModalAndResetForm, deleteAvatar, showErrorAlert],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Create user"
      actions={getUserModalActions({
        submitLabel: 'Create user',
        loadingLabel: 'Creating user…',
        onSubmit: handleSubmit(onFormSubmit),
        isSubmitting: formState.isSubmitting,
        isFormValid: formState.isValid,
      })}
      onClose={closeModalAndResetForm}
      data-testid="create-user-modal"
    >
      {modalProps?.partnerId !== null && modalProps?.partnerId !== undefined && (
        <AvatarUploader
          name={previewName}
          imageUrl={avatar.previewUrl}
          onFileChange={avatar.onFileChange}
        />
      )}
      <GenericForm<CreateUserFormModel> formMethods={formMethods}>
        <FormTextField
          type="textfield"
          name={CreateUserFormFields.FirstName}
          label="First name"
          required
          textFieldProps={{ inputProps: { 'data-testid': 'create-user-first-name' } }}
        />
        <FormTextField
          type="textfield"
          name={CreateUserFormFields.LastName}
          label="Last name"
          required
          textFieldProps={{ inputProps: { 'data-testid': 'create-user-last-name' } }}
        />
        <FormTextField
          type="textfield"
          name={CreateUserFormFields.Email}
          label="Email"
          required
          pattern={EMAIL_VALIDATION_REGEX}
          textFieldProps={{ inputProps: { 'data-testid': 'create-user-email' } }}
        />
        <FormPhoneInput
          type="phone"
          name={CreateUserFormFields.PhoneNumber}
          label="Phone"
          minLength={MIN_NUMBER_LENGTH}
          maxLength={MAX_NUMBER_LENGTH}
          textFieldProps={{ inputProps: { 'data-testid': 'create-user-phone' } }}
        />
      </GenericForm>
    </ModalContainer>
  );
};
